import React from 'react';
import {
  Box,
  Typography,
  LinearProgress,
  CircularProgress,
  alpha,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { gentlePulse, TIMING, EASING, BLURPLE } from './animations';

interface ActivationProgressProps {
  completed: number;
  total: number;
  variant?: 'linear' | 'circular' | 'dots';
  showLabel?: boolean;
  size?: 'small' | 'medium';
}

/**
 * Progress indicator for activation checklist
 * Shows completion progress in different visual styles
 *
 * Enhanced with:
 * - Shine animation on linear progress
 * - Pulsing effect on current dot
 * - BLURPLE accent color
 */
export function ActivationProgress({
  completed,
  total,
  variant = 'linear',
  showLabel = true,
  size = 'medium',
}: ActivationProgressProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const percentage = total > 0 ? (completed / total) * 100 : 0;
  const isComplete = completed >= total;

  if (variant === 'dots') {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        {Array.from({ length: total }).map((_, index) => {
          const isDone = index < completed;
          const isCurrent = index === completed && !isComplete;

          return (
            <Box
              key={index}
              sx={{
                width: size === 'small' ? 8 : 10,
                height: size === 'small' ? 8 : 10,
                borderRadius: '50%',
                bgcolor: isDone
                  ? theme.palette.success.main
                  : isCurrent
                    ? BLURPLE
                    : alpha(theme.palette.text.primary, 0.2),
                transition: `all ${TIMING.normal} ${EASING.smooth}`,
                transform: isCurrent ? 'scale(1.25)' : 'scale(1)',
                // Gentle pulse on current step
                ...(isCurrent && {
                  animation: `${gentlePulse} 2s ${EASING.smooth} infinite`,
                }),
              }}
            />
          );
        })}
        {showLabel && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ ml: 0.5 }}
          >
            {t('components.activation.progress', { completed, total })}
          </Typography>
        )}
      </Box>
    );
  }

  if (variant === 'circular') {
    const circularSize = size === 'small' ? 32 : 40;
    return (
      <Box sx={{ position: 'relative', display: 'inline-flex' }}>
        <CircularProgress
          variant="determinate"
          value={percentage}
          size={circularSize}
          thickness={4}
          sx={{
            color: 'primary.main',
            '& .MuiCircularProgress-circle': {
              strokeLinecap: 'round',
            },
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            bottom: 0,
            right: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography
            variant="caption"
            component="div"
            color="text.secondary"
            sx={{ fontWeight: 600, fontSize: size === 'small' ? 10 : 12 }}
          >
            {completed}/{total}
          </Typography>
        </Box>
      </Box>
    );
  }

  // Default: linear with gradient and shine animation
  return (
    <Box sx={{ width: '100%' }}>
      {showLabel && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="caption" color="text.secondary">
            {t('components.activation.progress', { completed, total })}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: isComplete ? theme.palette.success.main : 'text.secondary',
              fontWeight: isComplete ? 600 : 400,
            }}
          >
            {Math.round(percentage)}%
          </Typography>
        </Box>
      )}
      <LinearProgress
        variant="determinate"
        value={percentage}
        sx={{
          height: size === 'small' ? 4 : 6,
          borderRadius: 1,
          bgcolor: alpha(theme.palette.text.primary, 0.1),
          '& .MuiLinearProgress-bar': {
            borderRadius: 1,
            bgcolor: isComplete ? theme.palette.success.main : BLURPLE,
          },
        }}
      />
    </Box>
  );
}
